import { useParams, useNavigate } from "react-router-dom";
import { useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import { gql } from "@/__generated__";
import { SurveyTag } from "shared/src/data/survey-tags";
import Navbar from "@/components/layout/navbar/navbar";
import { ReferPatientProgressTracker } from "@/components/refer-patient-progress-tracker";
import { StrengthenReferral } from "../strengthen-referral/strengthen-referral";
import { PageFooter } from "@/components/layout/page-footer";
import {
  FilteredSurveyTab,
  FilteredSurveyTabHandle,
} from "../strengthen-referral/components/tabs/filtered-survey-tab/filtered-survey-tab";
import { flushSync } from "react-dom";
import { useFormContext } from "@/contexts/form-context";

const GET_APPEAL_STATUS = gql(`
  query GetAppealStatusForPrescriberInformation($appealId: ID!) {
    appealStatus(appealId: $appealId) {
      referred
    }
  }
`);

export function PrescriberInformation() {
  const { appealId } = useParams<{ appealId: string }>();
  const navigate = useNavigate();
  const surveyRef = useRef<FilteredSurveyTabHandle>(null);
  const { resetDirty } = useFormContext();
  const [isSaving, setIsSaving] = useState(false);

  const { data, loading: statusLoading } = useQuery(GET_APPEAL_STATUS, {
    variables: { appealId: appealId! },
    skip: !appealId,
    fetchPolicy: "no-cache",
  });

  const readOnly = data?.appealStatus?.referred ?? false;

  if (!appealId) {
    return null;
  }

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const success = await surveyRef.current?.save();
      if (success) {
        flushSync(() => {
          resetDirty();
        });
      }
      return success ?? false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveBeforeSend = async () => {
    return await handleSave();
  };

  const handleSaveAndNavigate = async () => {
    const success = await handleSave();
    if (success) {
      navigate(`/appeals/${appealId}/strengthen/medical-info/diagnosis`);
    }
  };

  const footer =
    !statusLoading && !readOnly ? (
      <PageFooter
        primaryButton={{
          text: "Save",
          onClick: handleSaveAndNavigate,
          loading: isSaving,
          disabled: isSaving,
        }}
      />
    ) : undefined;

  return (
    <Navbar
      titleComponent={
        <ReferPatientProgressTracker
          appealId={appealId}
          onBeforeSendReferral={handleSaveBeforeSend}
        />
      }
      footer={footer}
    >
      <StrengthenReferral
        appealId={appealId}
        activeTab="prescriber-information"
        tabContent={
          <FilteredSurveyTab
            ref={surveyRef}
            appealId={appealId}
            readOnly={readOnly}
            filterTag={SurveyTag.PRESCRIBER_INFORMATION}
          />
        }
      />
    </Navbar>
  );
}
